import React, { useState, useRef } from 'react';
import { Upload, X, Camera, FileImage, Loader, AlertCircle } from 'lucide-react';

export default function ImageUpload({ onUpload, onClose }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analysisText, setAnalysisText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setAnalysisError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file?.size > 10 * 1024 * 1024) {
      setAnalysisError('Image size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setAnalysisError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e?.target?.result);
    };
    reader?.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const analyzeImage = async () => {
    if (!selectedFile) return;

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      // Create a temporary URL for the image
      const imageUrl = URL.createObjectURL(selectedFile);
      
      // This is where you would integrate with OpenAI Vision API
      // For now, we'll simulate analysis
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock analysis result
      const mockAnalysis = `I can see what appears to be small insects on plant leaves. Based on the image characteristics, this looks like it could be aphids or whiteflies. The insects appear to be clustered on the underside of leaves, which is typical pest behavior. For accurate identification and treatment recommendations, I'll need to analyze this further.`;
      
      setAnalysisText(mockAnalysis);
      
      // Clean up temporary URL
      URL.revokeObjectURL(imageUrl);
      
    } catch (error) {
      console.error('Image analysis error:', error);
      setAnalysisError('Failed to analyze image. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) return;
    
    const finalAnalysisText = analysisText || 'Please analyze this image for pest identification and provide IPM recommendations.';
    onUpload(selectedFile, finalAnalysisText);
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreview(null);
    setAnalysisText('');
    setAnalysisError(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Upload Image for Pest Identification</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload a clear photo of the pest or plant damage for AI analysis
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* File Upload Area */}
          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef?.current?.click()}
            >
              <div className="space-y-4">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drop your image here or click to browse
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports JPG, PNG, WebP (max 10MB)
                  </p>
                </div>
                <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1">
                    <Camera className="w-4 h-4" />
                    <span>Take Photo</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FileImage className="w-4 h-4" />
                    <span>Upload File</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Image Preview */
            (<div className="space-y-4">
              <div className="relative">
                <img
                  src={preview}
                  alt="Uploaded for pest analysis"
                  className="w-full max-h-64 object-contain rounded-lg bg-gray-50"
                />
                <button
                  onClick={resetForm}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FileImage className="w-4 h-4" />
                <span>{selectedFile?.name}</span>
                <span>({(selectedFile?.size / 1024 / 1024)?.toFixed(1)} MB)</span>
              </div>
            </div>)
          )}

          {/* Analysis Section */}
          {selectedFile && (
            <div className="space-y-4">
              {!analysisText && !isAnalyzing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Camera className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-blue-900 mb-1">AI Image Analysis</h3>
                      <p className="text-sm text-blue-700 mb-3">
                        Let AI analyze your image to identify pests and suggest treatments before sending to chat.
                      </p>
                      <button
                        onClick={analyzeImage}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                      >
                        Analyze Image
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isAnalyzing && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                    <span className="text-sm text-gray-600">Analyzing image for pest identification...</span>
                  </div>
                </div>
              )}

              {analysisText && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    AI Analysis Result
                  </label>
                  <textarea
                    value={analysisText}
                    onChange={(e) => setAnalysisText(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows="4"
                    placeholder="AI analysis will appear here, or you can add your own description..."
                  />
                  <p className="text-xs text-gray-500">
                    You can edit this analysis before sending to chat
                  </p>
                </div>
              )}

              {analysisError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-red-700">{analysisError}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedFile || isAnalyzing}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              Send to Chat
            </button>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    </div>
  );
}